import React, { useEffect, useState } from 'react';
import currency from 'currency.js';
import cn from 'classnames';
import Item from './Item';
import ItemBuilder from './ItemBuilder';
import styles from './HolidayBuilder.module.scss';
import banner from './assets/holiday-banner.png';
import Button from '../../../Button/v2';
import { useEmailInterceptor } from '../../../EmailInterceptor/EmailInterceptor';
import { usePrevious } from '../../../PackageBuilders/MultiPose/hooks';
import discountedPrice, { grantsFreeAnnouncements } from '../../../../utils/offerPricing';
import gradAddonEligible from '../../../../utils/gradAddonEligibility';
import { addonUnitPrice } from '../../../../utils/functions';

// Announcements are identified by name -- the SAME rule the server uses to pick
// the free bundle (Cart#free_announcement_bundle_price), so "Free" here matches
// $0 at checkout.
export const isAnnouncement = (addon) => /announcement/i.test(addon?.name || '');

export function ItemSubtitle({ price, addon, offer, qualifiesForFreeAnnouncements = false }) {
  // One announcement bundle is free under a free-announcements offer, but ONLY
  // when the cart actually unlocks the perk (a senior portraits package > $1).
  // Server tells us via Cart#qualifies_for_free_announcements? so this display
  // matches Coupon.calculate_discount's charge exactly -- without the gate a
  // senior landing straight on /holiday saw every announcement crossed out as
  // Free before adding a package.
  if (offer && grantsFreeAnnouncements(offer) && qualifiesForFreeAnnouncements && isAnnouncement(addon)) {
    return (
      <div>
        <span className="text-gray-500 line-through decoration-2">{currency(price).format()}</span>{' '}
        <span className="font-extrabold text-green-700">Free</span>
      </div>
    );
  }

  const discounted = discountedPrice(price, offer);
  if (discounted != null) {
    // Attribution line under the strike-through so a customer knows WHY the
    // price dropped. Reads offer.value (the percentage) so any live percentage
    // coupon (GRAD27, CLASSOF27, ...) gets a truthful label without a hardcode.
    return (
      <div>
        <span className="font-bold text-gray-900">{currency(discounted).format()}</span>{' '}
        <span className="text-gray-500 line-through decoration-2">{currency(price).format()}</span>
        {Number(offer?.value) > 0 && (
          <div className="text-xs font-semibold" style={{ color: '#008fcf' }}>
            {`${Number(offer.value)}% off`}
          </div>
        )}
      </div>
    );
  }

  return <div>{currency(price).format()}</div>;
}

export default function ItemAdder({ mutation, addons, backgrounds, options, poses = [], siblingImages = [], onSelectBackground, offer = null, qualifiesForFreeAnnouncements = false, pricelistSeason = null, orderPackage = null }) {
  const [selectedAddonType, setSelectedAddonType] = useState('');
  const [addonSheetsPerType, setAddonSheetsPerType] = useState({});
  const [pose, setPose] = useState(null);
  const [background, setBackground] = useState(null);
  const { intercept } = useEmailInterceptor();

  const previousAddonType = usePrevious(selectedAddonType);

  // Filter addons before rendering. Senior-ness decided ONCE (pricelist season
  // / any senior pose — see gradAddonEligible for why NOT poses[2], whose
  // index lottery hid announcements).
  const seniorCustomer = gradAddonEligible({ pricelistSeason, poses });

  const filteredAddons = addons.filter((addon) => {
    if (addon.cssName === 'sibling') return false;

    // Grad announcements are senior-only products.
    if (addon._attributes.cssName.includes('grad')) return seniorCustomer;

    // Audience-priced rows: the same product exists per audience at different
    // prices (senior wallets $115 vs elementary $39.95) — show each customer
    // only their audience's row. Unflagged rows are for everyone.
    if (addon.isSenior && !addon.isElementary) return seniorCustomer;
    if (addon.isElementary && !addon.isSenior) return !seniorCustomer;

    return true;
  });

  const handleConfirmation = (addonSheets) => intercept(() => {
    setAddonSheetsPerType((prev) => ({ ...prev, [selectedAddonType]: addonSheets }));
    // setSelectedAddonType('');
    mutation.mutate({ addonSheets });
  });

  useEffect(() => {
    if (selectedAddonType === previousAddonType) return;

    setPose(null);
    setBackground(null);
  }, [selectedAddonType]);

  useEffect(() => {
    if (selectedAddonType === '') return;

    const selectablePoses = selectedAddonType === 'sibling' ? siblingImages : (poses.length > 0 ? poses : siblingImages);
    // const selectableBackgrounds = backgrounds.filter((bg) => bg.productTypeNames.includes(selectedAddonType));
    const selectableBackgrounds = backgrounds;

    setPose(selectablePoses[Math.floor(Math.random() * selectablePoses.length)]);
    setBackground(selectableBackgrounds[Math.floor(Math.random() * selectableBackgrounds.length)]);
    const interval = setInterval(() => {
      const rand = Math.round(Math.random());
      const changePose = rand === 0;

      if (changePose) {
        setPose(selectablePoses[Math.floor(Math.random() * selectablePoses.length)]);
      } else {
        setBackground(selectableBackgrounds[Math.floor(Math.random() * selectableBackgrounds.length)]);
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [selectedAddonType]);

  if (selectedAddonType !== '') {
    if (!pose) return null;
    if (!background) return null;

    const isSiblingPose = pose.id === siblingImages[0]?.id;

    return (
      <ItemBuilder
        isLoading={mutation.isLoading}
        isSiblingPose={isSiblingPose}
        selectedAddonType={selectedAddonType}
        // filteredAddons, NOT the raw list: senior/elementary variants share a
        // css_name, so filtering the raw prop here would show both audiences'
        // rows (at both prices) on the detail screen.
        addons={filteredAddons.filter((addon) => addon.cssName === selectedAddonType)}
        initialItems={addonSheetsPerType[selectedAddonType] || []}
        background={background}
        backgrounds={backgrounds}
        orderPackage={orderPackage}
        pose={pose}
        offer={offer}
        qualifiesForFreeAnnouncements={qualifiesForFreeAnnouncements}
        onConfirm={handleConfirmation}
        onCancelProps={{ onClick: () => setSelectedAddonType(''), text: 'Back' }}
      />
    );
  }

  /*useEffect(() => {
    addons.forEach(addon => {
      console.log(addon);
      console.log('Addon imageUrl:', addon._attributes.cssName);
    });
  }, [addons]);

  useEffect(() => {
    poses.forEach(pose => {
      console.log(pose);
      console.log('Pose imageUrl:', pose._attributes.url);
    });
  }, [poses]);*/

  const handleSelectBackground = () => intercept(() => {
    onSelectBackground(Object.values(addonSheetsPerType).flat());
  });

  return (
    <div className={cn(styles.page, { [styles.morePadding]: false }, selectedAddonType)}>
      <div className={styles.itemsWrapper}>
      {/* filteredAddons is THE addon filter — this render previously carried its own
          inline duplicate (gated on poses[0]) that had drifted from it, so fixes to
          one copy silently didn't reach the page. One implementation only. */}
      {filteredAddons.map((addon) => (
          <Item
            addon={addon}
            key={addon.name}
            titleText={addon.name}
            subtitleText={<ItemSubtitle price={addonUnitPrice(addon, orderPackage)} addon={addon} offer={offer} qualifiesForFreeAnnouncements={qualifiesForFreeAnnouncements} />}
            variant={addon.cssName}
            onClick={() => setSelectedAddonType(addon.cssName)}
            background={backgrounds[0]}
            pose={poses[0]}
          />
        ))}
      </div>
      {false && (
        <div className={styles.btnWrapper}>
          <Button onClick={handleSelectBackground}>Select Backgrounds</Button>
        </div>
      )}
    </div>
  );
}
